import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FaCheck, FaEye, FaEyeSlash, FaHistory, FaList, FaPen, FaTimes, FaUndo } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import { useEditMode } from '../../context/EditModeContext';
import './editable.css';

function StatusBadge({ isSaving, hasUnsavedChanges, saveError, saveMessage }) {
  if (isSaving) {
    return (
      <span className="edit-mode-banner__badge edit-mode-banner__badge--saving">
        <span className="edit-mode-banner__badge-dot" aria-hidden="true" />
        Saving
      </span>
    );
  }
  if (hasUnsavedChanges) {
    return <span className="edit-mode-banner__badge">Unsaved changes</span>;
  }
  if (saveError) {
    return (
      <span className="edit-mode-banner__badge edit-mode-banner__badge--error">{saveError}</span>
    );
  }
  return (
    <span className="edit-mode-banner__badge edit-mode-banner__badge--saved">
      <FaCheck aria-hidden="true" />
      {saveMessage || 'All changes saved'}
    </span>
  );
}

export default function EditModeBanner() {
  const { isEditing } = useAuth();
  const {
    resetContent,
    saveChanges,
    discardChanges,
    hasUnsavedChanges,
    isSaving,
    saveError,
  } = useContent();
  const {
    controlsVisible,
    panelOpen,
    bannerOpen,
    toggleControls,
    togglePanel,
    minimizeBanner,
    expandBanner,
  } = useEditMode();
  const [saveMessage, setSaveMessage] = useState('');

  if (!isEditing) return null;

  const handleSave = async () => {
    const result = await saveChanges();
    if (result.error) return;
    if (result.saved) {
      setSaveMessage('Saved just now');
      window.setTimeout(() => setSaveMessage(''), 2400);
    }
  };

  const handleDiscard = () => {
    if (!hasUnsavedChanges) return;
    if (window.confirm('Discard all unsaved changes on this page?')) {
      discardChanges();
      setSaveMessage('');
    }
  };

  const handleReset = async () => {
    if (hasUnsavedChanges) {
      const proceed = window.confirm(
        'You have unsaved changes. Reset all site content to defaults anyway?'
      );
      if (!proceed) return;
    }

    if (window.confirm('Reset all edited content back to defaults? This cannot be undone.')) {
      await resetContent();
      window.location.reload();
    }
  };

  if (!bannerOpen) {
    return createPortal(
      <button
        type="button"
        className="edit-mode-toolbar-chip"
        onClick={expandBanner}
        aria-label="Show editing toolbar"
      >
        <span className="edit-mode-toolbar-chip__icon" aria-hidden="true">
          <FaPen />
        </span>
        <span className="edit-mode-toolbar-chip__label">Edit tools</span>
        <StatusBadge
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          saveError={saveError}
          saveMessage={saveMessage}
        />
      </button>,
      document.body
    );
  }

  return createPortal(
    <div className="edit-mode-banner" role="status" aria-live="polite">
      <div className="edit-mode-banner__accent" aria-hidden="true" />

      <div className="edit-mode-banner__header">
        <div className="edit-mode-banner__brand">
          <span className="edit-mode-banner__icon" aria-hidden="true">
            <FaPen />
          </span>
          <div className="edit-mode-banner__copy">
            <span className="edit-mode-banner__title">Editing mode</span>
            <span className="edit-mode-banner__hint">
              Click any text to edit. Select text for bold, color, and links.
            </span>
          </div>
        </div>
        <button
          type="button"
          className="edit-mode-banner__close"
          onClick={minimizeBanner}
          aria-label="Hide editing toolbar"
        >
          <FaTimes aria-hidden="true" />
          <span>Hide</span>
        </button>
      </div>

      <div className="edit-mode-banner__status-row">
        <StatusBadge
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          saveError={saveError}
          saveMessage={saveMessage}
        />
      </div>

      <div className="edit-mode-banner__section">
        <span className="edit-mode-banner__section-label">View</span>
        <div className="edit-mode-banner__actions edit-mode-banner__actions--pair">
          <button
            type="button"
            className={`edit-mode-banner__btn edit-mode-banner__btn--ghost${panelOpen ? ' is-active' : ''}`}
            onClick={togglePanel}
            aria-pressed={panelOpen}
          >
            <FaList aria-hidden="true" />
            {panelOpen ? 'Hide panel' : 'Editor panel'}
          </button>

          <button
            type="button"
            className={`edit-mode-banner__btn edit-mode-banner__btn--ghost${controlsVisible ? ' is-active' : ''}`}
            onClick={toggleControls}
            aria-pressed={controlsVisible}
          >
            {controlsVisible ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
            {controlsVisible ? 'Hide highlights' : 'Show highlights'}
          </button>
        </div>
      </div>

      <div className="edit-mode-banner__section">
        <span className="edit-mode-banner__section-label">Changes</span>
        <div className="edit-mode-banner__actions edit-mode-banner__actions--save">
          <button
            type="button"
            className="edit-mode-banner__btn edit-mode-banner__btn--ghost"
            onClick={handleDiscard}
            disabled={!hasUnsavedChanges}
          >
            Discard
          </button>

          <button
            type="button"
            className="edit-mode-banner__btn edit-mode-banner__btn--primary"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
          >
            <FaCheck aria-hidden="true" />
            {isSaving ? 'Saving…' : 'Save now'}
          </button>
        </div>
      </div>

      <div className="edit-mode-banner__footer">
        <Link to="/admin/activity" className="edit-mode-banner__footer-link">
          <FaHistory aria-hidden="true" />
          Activity log
        </Link>
        <button
          type="button"
          className="edit-mode-banner__footer-link edit-mode-banner__footer-link--danger"
          onClick={handleReset}
        >
          <FaUndo aria-hidden="true" />
          Reset all content
        </button>
      </div>
    </div>,
    document.body
  );
}
